/**
 * PicksModals - All modal rendering for the PicksView.
 * Extracts chart modals, loading modal, tracking error modal, and share modal.
 */

import GameStatsChartModal from '@components/ui/GameStatsChartModal';
import PicksLoadingModal from '@components/ui/PicksLoadingModal';
import StatsChartModal from '@components/ui/StatsChartModal';
import { TrackingErrorModal } from '@components/ui/TrackingErrorModal';
import type { FailedLegInfo, NewParlayLeg } from '@hooks/useTrackedParlays';
import type { AnalysisMode } from '@parlay-picker/shared/analysisMode';
import type React from 'react';
import type { ChartClickData } from './ParlayCard';

type ChartModalData = ChartClickData;

interface TrackingErrorState {
  message: string;
  failedLegs: FailedLegInfo[];
  healthyLegs: NewParlayLeg[];
  parlayMetadata: {
    tier?: string;
    confidence?: string;
    reasoning?: string;
    sportsbook?: string;
  };
}

export interface PicksModalsProps {
  // Chart modal
  chartModal: ChartModalData | null;
  onCloseChart: () => void;

  // Picks loading modal
  isGenerating: boolean;
  analysisMode: AnalysisMode;
  onDismissGeneration: () => void;
  onCancelGeneration: () => void;
  isGenerationDismissed: boolean;

  // Tracking error recovery modal
  trackingError: TrackingErrorState | null;
  onCloseTrackingError: () => void;
  onTrackHealthyLegs: (
    legs: NewParlayLeg[],
    metadata: {
      tier?: string;
      confidence?: string;
      reasoning?: string;
      sportsbook?: string;
    },
  ) => Promise<void>;
  onAddToBuilder: (legs: NewParlayLeg[]) => void;
}

export function PicksModals({
  chartModal,
  onCloseChart,
  isGenerating,
  analysisMode,
  onDismissGeneration,
  onCancelGeneration,
  isGenerationDismissed,
  trackingError,
  onCloseTrackingError,
  onTrackHealthyLegs,
  onAddToBuilder,
}: PicksModalsProps): React.ReactElement {
  return (
    <>
      {/* Stats Chart Modal - conditionally render player or game modal */}
      {chartModal?.isGameProp && (
        <GameStatsChartModal
          key={`${chartModal.teamName}-${chartModal.propLine}-${chartModal.propType}`}
          leagueKey={chartModal.leagueKey}
          onClose={onCloseChart}
          teamName={chartModal.teamName ?? ''}
          prop={chartModal.prop ?? ''}
          recentGames={chartModal.teamRecentGames}
          h2hGames={chartModal.teamH2hGames}
          propLine={chartModal.propLine ?? undefined}
          propType={chartModal.propType ?? 'spread'}
          opponentAbbr={chartModal.opponentAbbr}
          overUnder={chartModal.overUnder}
          opponentTeamName={chartModal.opponentTeamName}
          opponentTeamRecentGames={chartModal.opponentTeamRecentGames}
          odds={chartModal.odds}
          marketKey={chartModal.marketKey}
        />
      )}
      {chartModal && !chartModal.isGameProp && (
        <StatsChartModal
          key={`${chartModal.playerName}-${chartModal.propLine}-${chartModal.statKey}`}
          leagueKey={chartModal.leagueKey}
          onClose={onCloseChart}
          playerName={chartModal.playerName ?? ''}
          prop={chartModal.prop ?? ''}
          recentGames={chartModal.recentGames}
          h2hGames={chartModal.h2hGames}
          propLine={chartModal.propLine ?? undefined}
          statKey={chartModal.statKey ?? undefined}
          opponentAbbr={chartModal.opponentAbbr}
          overUnder={chartModal.overUnder}
          teamName={chartModal.playerTeamName}
          odds={chartModal.odds}
          opponentDefRank={chartModal.opponentDefRank}
          marketKey={chartModal.marketKey}
        />
      )}

      {/* Picks Loading Modal */}
      <PicksLoadingModal
        isOpen={isGenerating}
        analysisMode={analysisMode}
        onDismiss={onDismissGeneration}
        onCancel={onCancelGeneration}
        isDismissed={isGenerationDismissed}
      />

      {/* Tracking Error Recovery Modal */}
      <TrackingErrorModal
        isOpen={trackingError !== null}
        onClose={onCloseTrackingError}
        errorMessage={trackingError?.message || ''}
        failedLegs={trackingError?.failedLegs || []}
        healthyLegs={trackingError?.healthyLegs || []}
        parlayMetadata={trackingError?.parlayMetadata || {}}
        onTrackHealthyLegs={onTrackHealthyLegs}
        onAddToBuilder={onAddToBuilder}
      />
    </>
  );
}
